import React, { useState, useEffect } from 'react';
import { fetchGeneratedDocuments, downloadGeneratedDocument } from './api';
import { GeneratedDocumentRead } from './types';
import { AppPagination } from '../../components/ui/AppPagination';
import {
  DownloadOutlined as DownloadIcon,
  HistoryOutlined as HistoryIcon,
  CheckCircleOutlined as CheckIcon,
  ErrorOutlineOutlined as ErrorIcon,
} from '@mui/icons-material';
import { formatDateTimeMsk } from '../../utils/date';

const PAGE_SIZE = 20;

const GeneratedDocumentsList: React.FC = () => {
  const [documents, setDocuments] = useState<GeneratedDocumentRead[]>([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalEstimated, setTotalEstimated] = useState(0);

  useEffect(() => {
    loadDocuments(page);
  }, [page]);

  const loadDocuments = async (currentPage: number) => {
    setLoading(true);
    try {
      const data = await fetchGeneratedDocuments(currentPage, PAGE_SIZE);
      setDocuments(data);

      const isMore = data.length === PAGE_SIZE;
      setHasMore(isMore);

      if (isMore) {
        setTotalEstimated(currentPage * PAGE_SIZE + 1);
      } else {
        setTotalEstimated((currentPage - 1) * PAGE_SIZE + data.length);
      }
    } catch (error) {
      console.error(error);
      alert('Ошибка при загрузке архива документов');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (id: number, fileName: string) => {
    try {
      await downloadGeneratedDocument(id, fileName);
    } catch (error) {
      alert('Ошибка при скачивании файла');
    }
  };

  const from = totalEstimated > 0 ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = Math.min(page * PAGE_SIZE, totalEstimated);

  return (
    <div className="w-full bg-white pb-10">
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8 space-y-6">

        <div className="flex items-center gap-3 mb-6">
          <HistoryIcon fontSize="large" className="text-[#1E61AF]" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Архив документов</h1>
            <p className="text-sm text-gray-500 mt-1">История всех сгенерированных файлов в системе</p>
          </div>
        </div>

        <div className="bg-white shadow-md border border-gray-100 rounded-lg overflow-hidden flex flex-col min-h-[500px]">

          <div className="flex-1 overflow-x-auto">
            <table className="w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500 font-medium">
                <tr>
                  <th className="px-6 py-4 text-left font-semibold">ID Генерации</th>
                  <th className="px-6 py-4 text-left font-semibold">Имя файла</th>
                  <th className="px-6 py-4 text-left font-semibold">Шаблон</th>
                  <th className="px-6 py-4 text-left font-semibold">Статус</th>
                  <th className="px-6 py-4 text-left font-semibold">Дата (МСК)</th>
                  <th className="px-6 py-4 text-right font-semibold">Действия</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                      </div>
                    </td>
                  </tr>
                ) : documents.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      Архив пуст или документы не найдены
                    </td>
                  </tr>
                ) : (
                  documents.map(doc => (
                    <tr key={doc.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-gray-900">
                        #{doc.id}
                      </td>
                      <td className="px-6 py-4 text-gray-900 max-w-[250px] truncate" title={doc.file_name}>
                        {doc.file_name}
                      </td>
                      <td className="px-6 py-4 text-gray-500 max-w-[200px] truncate" title={doc.template_name || ''}>
                        {doc.template_name || '—'}
                      </td>
                      <td className="px-6 py-4">
                        {doc.status === 'generated' ? (
                          <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-md text-xs font-medium">
                            <CheckIcon fontSize="inherit" /> Успешно
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-md text-xs font-medium">
                            <ErrorIcon fontSize="inherit" /> Ошибка
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-gray-500">
                        {formatDateTimeMsk(doc.created_at)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {doc.status === 'generated' && (
                          <button
                            onClick={() => handleDownload(doc.id, doc.file_name)}
                            className="text-[#1E61AF] hover:bg-blue-50 px-3 py-1.5 rounded-md transition-colors flex items-center justify-end gap-1.5 ml-auto font-medium"
                          >
                            <DownloadIcon fontSize="small" /> Скачать
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Подключаем ваш AppPagination */}
          {!loading && documents.length > 0 && (
            <div className="border-t border-gray-200 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4 bg-gray-50 mt-auto">
              <div className="text-sm text-gray-700">
                Показано <span className="font-medium">{from}</span> - <span className="font-medium">{to}</span>
                {hasMore ? ' из ...' : ` из ${totalEstimated}`}
              </div>
              <AppPagination
                page={page}
                totalItems={totalEstimated}
                pageSize={PAGE_SIZE}
                onChange={setPage}
              />
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default GeneratedDocumentsList;